import {
    useState,
    useEffect,
    useReducer,
    ChangeEvent,
    useRef,
    KeyboardEvent,
    useCallback,
    useMemo,
    startTransition,
    Reducer,
} from 'react';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import {
    GridColDef,
    GridActionsCellItem,
    GridPaginationModel,
    GridRowSelectionModel,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColumnVisibilityModel,
    GridSortModel,
    GridRenderCellParams,
} from '@mui/x-data-grid';
import {
    FormControl,
    FormHelperText,
    Box,
    CircularProgress,
    Typography,
    FormControlLabel,
    Checkbox,
    FormGroup,
    FormLabel,
} from '@mui/material';
import localforage from 'localforage';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseButton from '@/components/ui/BaseButton';
import BaseCard from '@/components/ui/BaseCard';
import BaseTextField from '@/components/form/BaseTextField';
import BaseTitle from '@/components/ui/BaseTitle';
import StoreFilterBlock from '@/components/store/StoreFilterBlock';
import NoStoreSelectionBlock from '@/components/store/NoStoreSelectionBlock';
import EditProductDialog from './EditProductDialog';
import ViewProductDialog from './ViewProductDialog';
import { Add, Delete, Visibility, Edit, Redeem, Upload, Download, WarningAmber, FilterList } from '@mui/icons-material';
import { useAppContext } from '@/provider/contexts';
import {
    getAllProducts,
    addNewProducts,
    removeProducts,
    editProducts,
    getExportProducts,
    importProducts,
    handleGetQueueJobStatus,
    importFamilyMartTfmCsvAPI,
    importLotussCsvAPI,
    importTaiwanMobileXlsxAPI,
} from '@/api/storeManagement/product';
import { fetchStoreDetailAPI } from '@/api/stores';
import BasePaper from '@/components/ui/BasePaper';
import '@/styles/pages/StoreMgmt/product/StoreProductPage.scss';
import { Product, StoreProductPageState, StoreProductPageAction } from '@/types/pages/storeManagement/product';
import MessageDialog from '@/components/feedback/MessageDialog';
import ImportDialog from './ImportDialog';
import { CorporationDetailResponse } from '@/types/pages/corporate';
import { Link } from 'react-router-dom';
import { fetchCorporationDetailAPI } from '@/api/corporate';
import { StoreInfo } from '@/types/pages/stores';
import BaseDialog from '@/components/ui/BaseDialog';
import PopoverMenu from '@/components/ui/PopoverMenu';
import { has, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import DeviceTableColumn from '@/components/ui/BaseTableColumn';
import { validateProductField } from './productValidation';
import { useOutletContext } from 'react-router-dom';
import { StoreMgmtOutletContext } from '@/types/pages/storeManagement/context';

const reducer: Reducer<StoreProductPageState, StoreProductPageAction> = (state, action) => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_MESSAGE_DIALOG_DATA':
            return { ...state, messageDialogData: action.payload };
        case 'SET_IS_REMOVE_DIALOG_OPEN':
            return { ...state, isRemoveDialogOpen: action.payload };
        case 'SET_INVALID_LIST':
            return { ...state, invalidList: action.payload };
        case 'SET_IS_IMPORT_DIALOG_OPEN':
            return { ...state, isImportDialogOpen: action.payload };
        case 'SET_IMPORT_LOADING':
            return { ...state, importLoading: action.payload };
        case 'SET_IMPORT_PROGRESS':
            return {
                ...state,
                importLoading: action.payload?.importLoading,
                importProgressPercentage: action.payload.importProgressPercentage,
            };
        case 'SET_IMPORT_PROGRESS_PERCENTAGE':
            return { ...state, importProgressPercentage: action.payload };
        case 'SET_EXPORT_LOADING':
            return { ...state, exportLoading: action.payload };
        case 'SET_SEARCH_QUERY':
            return { ...state, searchQuery: action.payload };
        case 'SET_PRODUCTS':
            return {
                ...state,
                loading: false,
                products: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_SORT_MODEL':
            return { ...state, sortModel: action.payload };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_JOB_ID_DATA':
            return {
                ...state,
                jobIdData: { ...action.payload },
            };
        case 'CLEAR_JOB_ID_DATA':
            return { ...state, importLoading: false, jobIdData: { jobId: '', isExistingJob: false } };
        case 'SET_IMPORT_FAMILY_MART_TFM_CSV_DATA':
            return { ...state, importFamilyMartTfmCsvData: action.payload };
        case 'SET_IMPORT_LOTUSS_CSV_DATA':
            return { ...state, importLotussCsvData: action.payload };
        case 'SET_IMPORT_TAIWAN_MOBILE_XLSX_DATA':
            return { ...state, importTaiwanMobileXlsxData: action.payload };
        case 'SET_VIEW_DIALOG_OPEN': {
            const { open, barcode } = action.payload;
            return { ...state, isViewDialogOpen: open, currentBarCode: barcode ?? '' };
        }
        case 'SET_JSON_IMPORT_ERROR_MSG': {
            return { ...state, importJsonErrorMsg: action.payload };
        }
        default:
            return state;
    }
};

const defaultState: StoreProductPageState = {
    loading: false,
    messageDialogData: {
        isOpen: false,
        message: '',
    },
    isRemoveDialogOpen: false,
    invalidList: [],
    isImportDialogOpen: false,
    importLoading: false,
    importProgressPercentage: -1,
    exportLoading: false,
    searchQuery: {
        f_store: '',
        f_barcode: '',
        f_number: '',
        f_name: '',
        f_unit: '',
        f_area: '',
        f_price: '',
        f_promo_price: '',
        f_is_hq: '0',
    },
    products: [],
    rowCount: 0,
    sortModel: { field: null, sort: null },
    paginationModel: {
        page: 0,
        pageSize: 5,
    },
    jobIdData: {
        jobId: '',
        isExistingJob: false,
    },
    importJsonErrorMsg: '',
    importFamilyMartTfmCsvData: { messageType: '' },
    importLotussCsvData: { messageType: '' },
    importTaiwanMobileXlsxData: { messageType: '' },
    isViewDialogOpen: false,
    currentBarCode: '',
};

const StoreProductPage = () => {
    const { manageStoreId, setNotification } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product>({} as Product);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const { t } = useTranslation('common');

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        barcode: true,
        number: true,
        alternate_number1: false,
        alternate_number2: false,
        name: true,
        area: true,
        unit: true,
        price: true,
        promo_price: false,
        custom_fields: false,
        default_layout: true,
        action: true,
    });

    const [newProductData, setNewProductData] = useState({
        Number: '',
        Barcode: '',
        Name: '',
        Area: '',
        Unit: '',
        Price: '',
        'Promo Price': '',
        'Default Layout': '',
        'Custom Fields': '',
    });

    const [validatedMessage, setValidatedMessage] = useState<{ [key: string]: string | null }>({});

    const checkIsRequired = (key: string) => {
        return key === 'Number' || key === 'Barcode' || key === 'Name' || key === 'Price';
    };

    const getValidationMessage = (key: string, value: string) => {
        if (checkIsRequired(key) && !value) return 'Required';

        const field = (
            {
                Number: 'number',
                Barcode: 'barcode',
                Name: 'name',
                Area: 'area',
                Unit: 'unit',
                Price: 'price',
                'Promo Price': 'promo_price',
                'Default Layout': 'default_layout',
            } as Record<string, string>
        )[key];

        return field ? validateProductField(field, value) : null;
    };

    const [
        {
            loading,
            messageDialogData,
            isRemoveDialogOpen,
            invalidList,
            isImportDialogOpen,
            importLoading,
            importProgressPercentage,
            exportLoading,
            searchQuery,
            products,
            rowCount,
            sortModel,
            paginationModel,
            jobIdData,
            importFamilyMartTfmCsvData,
            importLotussCsvData,
            importTaiwanMobileXlsxData,
            isViewDialogOpen,
            currentBarCode,
            importJsonErrorMsg,
        },
        dispatch,
    ] = useReducer(reducer, defaultState);

    // Local state for search form to prevent lag when typing
    const [searchForm, setSearchForm] = useState(searchQuery);

    // Use ref to access latest searchForm without causing callback recreation
    const searchFormRef = useRef(searchForm);
    searchFormRef.current = searchForm;
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);
    const { storeSettings, isStoreReady, formatUtcToStoreTime } = useOutletContext<StoreMgmtOutletContext>();
    // Sync local searchForm with reducer state when it changes externally
    useEffect(() => {
        setSearchForm(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        const jobId = jobIdData.jobId;

        if (!jobId) return;

        let isActive = true;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const resetImportProgress = () => {
            dispatch({
                type: 'SET_IMPORT_PROGRESS',
                payload: {
                    importLoading: false,
                    importProgressPercentage: -1,
                },
            });

            dispatch({ type: 'CLEAR_JOB_ID_DATA' });
        };

        const finishImport = (isSuccess: boolean = true) => {
            resetImportProgress();

            setNotification({
                message: isSuccess ? t('Import completed successfully.') : t('Import failed.'),
                severity: isSuccess ? 'success' : 'error',
            });

            if (isSuccess) {
                fetchProducts();
            }
        };

        const poll = async () => {
            try {
                const data = await fetchImportProgress(jobId);

                if (!isActive) {
                    return;
                }

                if (typeof data.percentage !== 'number') {
                    throw new Error('Invalid import progress response.');
                }

                const percentage = Math.round(data.percentage);

                dispatch({
                    type: 'SET_IMPORT_PROGRESS_PERCENTAGE',
                    payload: percentage,
                });

                if (percentage < 100) {
                    timer = setTimeout(poll, 1000);
                    return;
                }

                finishImport();
            } catch {
                if (!isActive) return;

                finishImport(false);
            }
        };

        poll();

        return () => {
            isActive = false;

            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [jobIdData.jobId]);

    // Memoize fetchProducts to prevent recreation
    // Accepts optional queryOverride to use updated search form immediately
    const fetchProducts = useCallback(
        async (queryOverride?: typeof searchQuery) => {
            fetchAbortControllerRef.current?.abort();
            if (!manageStoreId) return;

            const fetchId = latestFetchIdRef.current + 1;
            latestFetchIdRef.current = fetchId;

            const abortController = new AbortController();
            fetchAbortControllerRef.current = abortController;
            dispatch({ type: 'SET_LOADING', payload: true });
            const corporationId = (await localforage.getItem('corporation_id')) as number;

            try {
                const params = {
                    ...(queryOverride ?? searchQuery),
                    f_store: manageStoreId,
                    current_page: paginationModel.page + 1,
                    data_per_page: paginationModel.pageSize,
                    sort_field: sortModel.field,
                    sort_order: sortModel.sort,
                };
                const {
                    data: { data },
                } = await getAllProducts(corporationId, params, { signal: abortController.signal });

                if (latestFetchIdRef.current !== fetchId) return;
                dispatch({
                    type: 'SET_PRODUCTS',
                    payload: data,
                });
            } catch (error) {
                if (abortController.signal.aborted) return;
                console.error('Error fetching products:', error);
            } finally {
                if (latestFetchIdRef.current === fetchId) {
                    dispatch({ type: 'SET_LOADING', payload: false });
                    fetchAbortControllerRef.current = null;
                }
            }
        },
        [manageStoreId, searchQuery, paginationModel, sortModel],
    );
    const clearProducts = async (changeStore = false) => {
        dispatch({
            type: 'SET_SEARCH_QUERY',
            payload: {
                ...searchQuery,
                f_barcode: '',
                f_number: '',
                f_name: '',
                f_area: '',
                f_unit: '',
                f_is_hq: changeStore ? '0' : searchQuery.f_is_hq,
            },
        });
        dispatch({
            type: 'SET_PAGINATION_MODEL',
            payload: { page: 0, pageSize: 5 },
        });
        dispatch({
            type: 'SET_SORT_MODEL',
            payload: { field: null, sort: null },
        });
    };

    // Update search form handler - updates local state immediately for responsive UI
    const updateSearchForm = useCallback((field: keyof typeof searchQuery, value: string) => {
        setSearchForm((prev) => {
            const updated = { ...prev, [field]: value };
            searchFormRef.current = updated;
            return updated;
        });
    }, []);

    // Apply search and fetch - syncs local state to reducer and fetches.
    // fetchProducts is called with an explicit queryOverride rather than
    // relying on the SET_SEARCH_QUERY dispatch above having landed first
    // (it's wrapped in startTransition, so searchQuery in this closure can
    // still be the pre-search value when fetchProducts would otherwise run).
    const applySearchAndFetch = useCallback(async () => {
        const currentSearchForm = searchFormRef.current;
        startTransition(() => {
            dispatch({ type: 'SET_SEARCH_QUERY', payload: currentSearchForm });
        });
        await fetchProducts(currentSearchForm);
    }, [fetchProducts]);

    // Memoize keyUpHandler to prevent recreation on every render
    const keyUpHandler = useCallback(
        (event: KeyboardEvent<Element>) => {
            if (event.key === 'Enter') {
                applySearchAndFetch();
            }
        },
        [applySearchAndFetch],
    );

    const pageChangeHandler = useCallback(
        (pageModel: GridPaginationModel) => {
            if (paginationModel.pageSize !== pageModel.pageSize) {
                // When the table page size changes, reset the page to 1.
                dispatch({
                    type: 'SET_PAGINATION_MODEL',
                    payload: { ...pageModel, page: 0 },
                });
            } else {
                dispatch({ type: 'SET_PAGINATION_MODEL', payload: pageModel });
            }
        },
        [paginationModel.pageSize, dispatch],
    );

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
        const value = e.target.value;

        setNewProductData((prevData) => ({
            ...prevData,
            [key]: value,
        }));

        setValidatedMessage((prevMessage) => ({
            ...prevMessage,
            [key]: getValidationMessage(key, value),
        }));
    };

    const sortModelChangeHandler = useCallback(
        (sortModel: GridSortModel) => {
            if (sortModel.length > 0) {
                const { field, sort } = sortModel[0];
                dispatch({
                    type: 'SET_SORT_MODEL',
                    payload: { field: field, sort: sort as 'asc' | 'desc' },
                });
            } else {
                dispatch({
                    type: 'SET_SORT_MODEL',
                    payload: { field: null, sort: null },
                });
            }
        },
        [dispatch],
    );

    const formValidate = () => {
        const newValidatedMessage: { [key: string]: string | null } = {};
        let isFormValid = true;

        (Object.keys(newProductData) as (keyof typeof newProductData)[]).forEach((key) => {
            const message = getValidationMessage(key, newProductData[key]);
            if (message) {
                isFormValid = false;
                newValidatedMessage[key] = message;
            }
        });

        setValidatedMessage(newValidatedMessage);
        return isFormValid;
    };

    const confirmRemoveProducts = async () => {
        const selectedProductsId = selectedRows as number[];

        const params = {
            store_id: manageStoreId as number,
            product_ids: selectedProductsId,
        };

        setIsConfirmDialogOpen(false);
        dispatch({ type: 'SET_LOADING', payload: true });

        const response = await removeProducts(params);
        await fetchProducts();
        setSelectedRows([]);

        if (response.status === 200) {
            const invalid = response.data.data.invalid || [];
            if (invalid.length > 0) {
                dispatch({ type: 'SET_IS_REMOVE_DIALOG_OPEN', payload: true });
                dispatch({ type: 'SET_INVALID_LIST', payload: invalid });
                setNotification({
                    message: t('Removing {{count}} products failed.', { count: selectedRows.length }),
                    severity: 'error',
                });
            } else {
                setNotification({
                    message: t('{{count}} products removed successfully.', { count: selectedRows.length }),
                    severity: 'success',
                });
            }
        } else {
            setNotification({
                message: t('Removing {{count}} products failed.', { count: selectedRows.length }),
                severity: 'error',
            });
        }
    };

    const handleAddProduct = async () => {
        if (!formValidate()) return;

        const newProduct = {
            shelf_id: [],
            name: newProductData.Name,
            area: newProductData.Area || '',
            date_updated: new Date().toISOString().slice(0, 19).replace('T', ' '),
            price: parseFloat(newProductData.Price),
            barcode: newProductData.Barcode,
            number: newProductData.Number,
            default_layout: isEmpty(newProductData['Default Layout']) ? '' : Number(newProductData['Default Layout']),
            custom_fields: newProductData['Custom Fields'] || '',
            current_shelf_id: [],
            unit: newProductData.Unit,
            promo_price: newProductData['Promo Price'] ? parseFloat(newProductData['Promo Price']) : '',
        };

        setIsModalOpen(false);
        dispatch({ type: 'SET_LOADING', payload: true });
        const response = await addNewProducts(manageStoreId as number, [newProduct]);
        await fetchProducts();

        if (response.status === 200) {
            setNotification({
                message: t('Add one product successfully.'),
                severity: 'success',
            });
        } else {
            setNotification({
                message: t('Something went wrong. Failed to add product.'),
                severity: 'error',
            });
        }
    };
    const clearNewProductData = () => {
        setNewProductData({
            Number: '',
            Barcode: '',
            Name: '',
            Area: '',
            Unit: '',
            Price: '',
            'Promo Price': '',
            'Default Layout': '',
            'Custom Fields': '',
        });
    };

    const importClickHandler = () => {
        dispatch({ type: 'SET_IS_IMPORT_DIALOG_OPEN', payload: true });
    };

    const fetchImportProgress = async (jobId: string) => {
        const {
            data: { data, message, isSuccess },
        } = await handleGetQueueJobStatus(manageStoreId as number, jobId);

        if (isSuccess) return data;

        throw new Error(message);
    };

    const importHandler = async (event: ChangeEvent<HTMLInputElement>, fileType: string) => {
        const file = event.target.files?.[0];

        if (file) {
            if (fileType === 'aura-json') {
                await importAuraJson(file);
            }

            if (fileType === 'familyMart-tfm-csv') {
                let corpDetails = (await localforage.getItem('corporation_details')) as CorporationDetailResponse;

                if (!corpDetails) {
                    const corporationId = (await localforage.getItem('corporation_id')) as number;
                    const {
                        data: { data, isSuccess },
                    } = await fetchCorporationDetailAPI(corporationId);

                    if (isSuccess) {
                        corpDetails = data;
                        localforage.setItem('corporation_details', data);
                    }
                }

                // Compare store code from uploaded file name
                const storeInfo = await getStoreDetails();
                const isCorrectFileName = event.target.files?.[0].name.startsWith('TFM.');
                const fileNameStoreCode = event.target.files?.[0].name.split('.')[1];
                if (storeInfo?.store_code === fileNameStoreCode) {
                    await importFamilyMartTfmCsv(
                        file,
                        corpDetails.preproc_code,
                        event.target.files?.[0].name as string,
                    );
                } else if (isCorrectFileName && fileNameStoreCode) {
                    const message = t(
                        'The uploaded file (ID: {{fileId}}) does not match the current store (ID: {{storeId}}).',
                        {
                            fileId: fileNameStoreCode,
                            storeId: storeInfo?.store_code,
                        },
                    );
                    dispatch({ type: 'SET_MESSAGE_DIALOG_DATA', payload: { isOpen: true, message } });
                } else {
                    const message = t("This file doesn't look like a product file.");
                    dispatch({ type: 'SET_MESSAGE_DIALOG_DATA', payload: { isOpen: true, message } });
                }
            }
            if (fileType === 'lotuss-thailand') {
                let corpDetails = (await localforage.getItem('corporation_details')) as CorporationDetailResponse;

                if (!corpDetails) {
                    const corporationId = (await localforage.getItem('corporation_id')) as number;
                    const {
                        data: { data, isSuccess },
                    } = await fetchCorporationDetailAPI(corporationId);

                    if (isSuccess) {
                        corpDetails = data;
                        localforage.setItem('corporation_details', data);
                    }
                }
                const fileName = file.name.split('.').pop();
                if (fileName === 'csv') {
                    await importLotussCsv(file, corpDetails.preproc_code, file.name as string);
                } else {
                    const message = `This file doesn't look like a product file.`;
                    dispatch({ type: 'SET_MESSAGE_DIALOG_DATA', payload: { isOpen: true, message } });
                }
            }
            if (fileType === 'taiwan-mobile') {
                let corpDetails = (await localforage.getItem('corporation_details')) as CorporationDetailResponse;

                if (!corpDetails) {
                    const corporationId = (await localforage.getItem('corporation_id')) as number;
                    const {
                        data: { data, isSuccess },
                    } = await fetchCorporationDetailAPI(corporationId);

                    if (isSuccess) {
                        corpDetails = data;
                        localforage.setItem('corporation_details', data);
                    }
                }
                const fileName = event.target.files?.[0].name as string;
                const type = fileName.split('.').pop();
                if (type === 'xlsx') {
                    await importTaiwanMobileXlsx(file, corpDetails.preproc_code, fileName);
                } else {
                    const message = `This file doesn't look like a product file.`;
                    dispatch({ type: 'SET_MESSAGE_DIALOG_DATA', payload: { isOpen: true, message } });
                }
            }
        }
    };
    const importAuraJson = async (file: Blob) => {
        if (file) {
            dispatch({
                type: 'SET_IMPORT_PROGRESS',
                payload: { importLoading: true, importProgressPercentage: 0 },
            });
            dispatch({
                type: 'SET_JSON_IMPORT_ERROR_MSG',
                payload: '',
            });
            dispatch({ type: 'SET_IS_IMPORT_DIALOG_OPEN', payload: false });

            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const importData = JSON.parse(e.target?.result as string);
                    const {
                        data: { data, isSuccess },
                    } = await importProducts({
                        store_id: manageStoreId as number,
                        products: importData,
                    });
                    const { existing_job_id, job_id } = data || {};
                    if (isSuccess && !existing_job_id) {
                        setNotification({
                            message: t('Import started successfully. Processing may take a few minutes.'),
                            severity: 'info',
                        });

                        dispatch({
                            type: 'SET_JOB_ID_DATA',
                            payload: { jobId: job_id, isExistingJob: false },
                        });
                        return;
                    }
                    if (existing_job_id) {
                        dispatch({
                            type: 'SET_JOB_ID_DATA',
                            payload: { jobId: existing_job_id, isExistingJob: true },
                        });
                        return;
                    }
                    throw new Error();
                } catch (error: unknown) {
                    if (error instanceof Error) {
                        setNotification({ message: error.message || t('File import failed.'), severity: 'error' });
                        dispatch({
                            type: 'SET_JSON_IMPORT_ERROR_MSG',
                            payload: error.message,
                        });
                        dispatch({
                            type: 'SET_IMPORT_PROGRESS',
                            payload: { importLoading: false, importProgressPercentage: -1 },
                        });
                        dispatch({ type: 'CLEAR_JOB_ID_DATA' });
                        return;
                    }
                    console.error('Error Import Files', error);
                }
            };
            reader.readAsText(file, 'UTF-8');
        }
    };

    const importFamilyMartTfmCsv = async (file: Blob, preProcCode: string, filename: string) => {
        if (file) {
            dispatch({ type: 'SET_IMPORT_LOADING', payload: true });
            dispatch({ type: 'SET_IS_IMPORT_DIALOG_OPEN', payload: false });

            const {
                data: { data, isSuccess, message },
            } = await importFamilyMartTfmCsvAPI(file, preProcCode, filename);

            if (isSuccess && data.sync_result[0].can_process) {
                dispatch({
                    type: 'SET_IMPORT_FAMILY_MART_TFM_CSV_DATA',
                    payload: {
                        messageType: 'success',
                    },
                });
            } else if (!data.sync_result[0].can_process) {
                dispatch({
                    type: 'SET_IMPORT_FAMILY_MART_TFM_CSV_DATA',
                    payload: {
                        messageType: 'previous-ongoing',
                    },
                });
            } else {
                setNotification({
                    message: message,
                    severity: 'error',
                });
            }
            dispatch({ type: 'SET_IMPORT_LOADING', payload: false });
        }
    };
    const renderJsonImportError = () => {
        if (importJsonErrorMsg) {
            return (
                <>
                    <Typography variant="body1" color="error">
                        {importJsonErrorMsg}
                    </Typography>
                </>
            );
        }
        return <></>;
    };
    const renderImportFamilyMartTfmCsv = () => {
        switch (importFamilyMartTfmCsvData.messageType) {
            case 'success':
                return (
                    <Typography variant="body1" color="success">
                        🗹 {t('File received. Import status will appear in')}
                        <Link className="to-history-link" to="/store_management/history">
                            {t('Activity Log')}
                        </Link>{' '}
                        {t('soon.')}
                    </Typography>
                );
            case 'previous-ongoing':
                return (
                    <Typography variant="body1" color="error">
                        {t('An ongoing product import was detected. Resuming the previous product import.')}
                    </Typography>
                );
            default:
                return <></>;
        }
    };
    const importLotussCsv = async (file: Blob, preProcCode: string, filename: string) => {
        if (file) {
            dispatch({ type: 'SET_IMPORT_LOADING', payload: true });
            dispatch({ type: 'SET_IS_IMPORT_DIALOG_OPEN', payload: false });

            const {
                data: { data, isSuccess, message },
            } = await importLotussCsvAPI(file, preProcCode, filename);
            if (isSuccess && data.sync_result[0].can_process) {
                dispatch({
                    type: 'SET_IMPORT_LOTUSS_CSV_DATA',
                    payload: {
                        messageType: 'success',
                    },
                });
            } else if (!data.sync_result[0].can_process) {
                dispatch({
                    type: 'SET_IMPORT_LOTUSS_CSV_DATA',
                    payload: {
                        messageType: 'previous-ongoing',
                    },
                });
            } else {
                setNotification({
                    message: message,
                    severity: 'error',
                });
            }
            dispatch({ type: 'SET_IMPORT_LOADING', payload: false });
        }
    };
    const renderImportLotussTfmCsv = () => {
        switch (importLotussCsvData.messageType) {
            case 'success':
                return (
                    <Typography variant="body1" color="success">
                        🗹 File received. Import status will appear in
                        <Link className="to-history-link" to="/store_management/history">
                            Activity Log
                        </Link>{' '}
                        soon.
                    </Typography>
                );
            case 'previous-ongoing':
                return (
                    <Typography variant="body1" color="error">
                        An ongoing product import was detected. Resuming the previous product import.
                    </Typography>
                );
            default:
                return <></>;
        }
    };
    const importTaiwanMobileXlsx = async (file: Blob, preProcCode: string, filename: string) => {
        if (file) {
            dispatch({ type: 'SET_IMPORT_LOADING', payload: true });
            dispatch({ type: 'SET_IS_IMPORT_DIALOG_OPEN', payload: false });
            const storeInfo = await getStoreDetails();
            const {
                data: { data, isSuccess, message },
            } = await importTaiwanMobileXlsxAPI(file, preProcCode, (storeInfo as StoreInfo).store_code, filename);
            if (isSuccess) {
                const result = data.sync_result && data.sync_result[0];
                if (result.can_process) {
                    dispatch({
                        type: 'SET_IMPORT_TAIWAN_MOBILE_XLSX_DATA',
                        payload: {
                            messageType: 'success',
                        },
                    });
                } else {
                    dispatch({
                        type: 'SET_IMPORT_TAIWAN_MOBILE_XLSX_DATA',
                        payload: {
                            messageType: 'previous-ongoing',
                        },
                    });
                }
            } else {
                setNotification({
                    message: message,
                    severity: 'error',
                });
            }
            dispatch({ type: 'SET_IMPORT_LOADING', payload: false });
        }
    };
    const renderImportTaiwanMobileXlsx = () => {
        switch (importTaiwanMobileXlsxData.messageType) {
            case 'success':
                return (
                    <Typography variant="body1" color="success">
                        🗹 File received. Import status will appear in
                        <Link className="to-history-link" to="/store_management/history">
                            Activity Log
                        </Link>{' '}
                        soon.
                    </Typography>
                );
            case 'previous-ongoing':
                return (
                    <Typography variant="body1" color="error">
                        An ongoing product import was detected. Resuming the previous product import.
                    </Typography>
                );
            default:
                return <></>;
        }
    };
    const exportHandler = async () => {
        try {
            dispatch({ type: 'SET_EXPORT_LOADING', payload: true });
            const corporationId = (await localforage.getItem('corporation_id')) as number;
            const storeInfo = await getStoreDetails();
            const { data } = await getExportProducts(corporationId, manageStoreId as number);
            /**
             * Backed format with two structures of data:
             * 1. data:{ status: 400 || 500, message: 'error message' , data: {...} } -> export Failed with reason
             * 2. data:{ 0: {...}, 1: {...}, ... } -> export Success
             */
            if (isEmpty(data) || has(data, 'status') || has(data, 'message')) {
                throw new Error(data?.message);
            }
            const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
            const fileName = buildExportFileName(storeInfo);
            saveAs(blob, `${fileName}.json`);
            setNotification({ message: t('File exported successfully.'), severity: 'success' });
        } catch (error) {
            console.error('Error exporting products:', error);
            setNotification({ message: (error as Error).message || t('File export failed.'), severity: 'error' });
        } finally {
            dispatch({ type: 'SET_EXPORT_LOADING', payload: false });
        }
    };

    const buildExportFileName = (storeInfo?: StoreInfo): string => {
        const now = dayjs();
        const exportDate = now.format('YYYYMMDD');
        const exportTime = now.format('HHmmss');
        return ['aura_products', storeInfo?.store_name, storeInfo?.store_code, exportDate, exportTime]
            .filter((value) => value !== null && value !== undefined && value !== '')
            .join('_');
    };

    const getStoreDetails = async () => {
        if (!manageStoreId) return;
        const storeId = manageStoreId;

        const {
            data: { data, isSuccess },
        } = await fetchStoreDetailAPI(storeId);

        if (isSuccess) {
            return data.store_info;
        }
    };

    const handleEditProduct = useCallback((product: Product) => {
        setSelectedProduct(product);
        setIsEditDialogOpen(true);
    }, []);

    const handleViewProduct = useCallback(
        async (product: Product) => {
            dispatch({ type: 'SET_VIEW_DIALOG_OPEN', payload: { open: true, barcode: product.barcode } });
        },
        [dispatch],
    );

    // Memoize handleViewProductClose to prevent recreation
    const handleViewProductClose = useCallback(() => {
        dispatch({ type: 'SET_VIEW_DIALOG_OPEN', payload: { open: false } });
    }, [dispatch]);

    const confirmEditProduct = async () => {
        setIsEditDialogOpen(false);

        const editProduct = [
            {
                shelf_id: [],
                name: selectedProduct.name,
                area: selectedProduct.area,
                date_updated: new Date().toISOString().slice(0, 19).replace('T', ' '),
                price: selectedProduct.price,
                barcode: selectedProduct.barcode,
                number: selectedProduct.number,
                default_layout: selectedProduct.default_layout,
                custom_fields: selectedProduct.custom_fields,
                unit: selectedProduct.unit,
                promo_price: selectedProduct.promo_price,
            },
        ];

        dispatch({ type: 'SET_LOADING', payload: true });
        const {
            data: { data, isSuccess, message },
        } = await editProducts(manageStoreId as number, editProduct);
        await fetchProducts();
        if (isSuccess) {
            setNotification({
                message: t('Edit product successfully.'),
                severity: 'success',
            });

            return;
        }

        const reason = data?.barcodes?.blocked?.[0]?.reason || '';
        setNotification({
            message: reason || message,
            severity: 'error',
        });
    };

    // clear new product data when modal is closed
    useEffect(() => {
        if (!isModalOpen) {
            clearNewProductData();
        }
    }, [isModalOpen]);

    // clear SelectedProduct when modal is closed
    useEffect(() => {
        if (!isEditDialogOpen) {
            setSelectedProduct({} as Product);
        }
    }, [isEditDialogOpen]);

    // isStoreReady and fetchProducts are intentionally omitted below:
    // fetchProducts's own identity changes whenever manageStoreId changes
    // (see its useCallback deps above), so listing either here would still
    // fire this effect a render early, with the new store id but the
    // previous store's stale searchQuery/paginationModel attached. On a
    // store switch, the clear-on-store-change effect further down resets
    // those; the resulting paginationModel change is what re-triggers this
    // effect with the correct, already-cleared filters (mirrors
    // StoreDevicePage.tsx's fetch effect, which is gated the same way).
    // Unlike Device/History, this fetch doesn't need isTimezoneReady:
    // formatUtcToStoreTime here is only used at render time (passed to
    // ViewProductDialog), not baked into fetched/stored data.
    useEffect(() => {
        if (!isStoreReady) return;
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortModel, paginationModel, searchQuery.f_is_hq]);

    const isFirstStoreRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstStoreRenderRef.current) {
            isFirstStoreRenderRef.current = false;
            return;
        }
        clearProducts(true);
    }, [manageStoreId]);

    useEffect(() => {
        dispatch({ type: 'CLEAR_JOB_ID_DATA' });
        dispatch({ type: 'SET_IMPORT_FAMILY_MART_TFM_CSV_DATA', payload: { messageType: '' } });
        dispatch({ type: 'SET_IMPORT_LOTUSS_CSV_DATA', payload: { messageType: '' } });
        dispatch({ type: 'SET_IMPORT_TAIWAN_MOBILE_XLSX_DATA', payload: { messageType: '' } });
        dispatch({ type: 'SET_JSON_IMPORT_ERROR_MSG', payload: '' });
    }, [manageStoreId]);

    const renderSimpleCell = useCallback((param: GridRenderCellParams) => {
        const value = param.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <DeviceTableColumn columnValue={displayValue} />;
    }, []);

    const renderDefaultLayoutCell = useCallback(
        (param: GridRenderCellParams) => {
            const value = param.value;
            const displayValue = value === null || value === undefined || value === '' ? t('Not set') : String(value);
            return <DeviceTableColumn columnValue={displayValue} />;
        },
        [t],
    );

    // Memoize columns to prevent recreation on every render
    const columns: GridColDef[] = useMemo(
        () => [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                hideable: false,
            },
            { field: 'barcode', headerName: t('Barcode'), flex: 1, renderCell: renderSimpleCell },
            { field: 'number', headerName: t('Number'), flex: 1, renderCell: renderSimpleCell },
            // {
            //     field: 'alternate_number1',
            //     headerName: 'Alternate Number 1',
            //     flex: 1,
            // },
            // {
            //     field: 'alternate_number2',
            //     headerName: 'Alternate Number 2',
            //     flex: 1,
            // },
            { field: 'name', headerName: t('Name'), flex: 1, renderCell: renderSimpleCell },
            { field: 'area', headerName: t('Area'), flex: 1, renderCell: renderSimpleCell },
            { field: 'unit', headerName: t('Unit'), flex: 1, renderCell: renderSimpleCell },
            { field: 'price', headerName: t('Price'), flex: 1, renderCell: renderSimpleCell },
            // { field: 'promo_price', headerName: 'Promo Price', flex: 1 },
            // { field: 'custom_fields', headerName: 'Custom Fields', flex: 1 },
            {
                field: 'default_layout',
                headerName: t('Default Layout'),
                flex: 1,
                renderCell: renderDefaultLayoutCell,
            },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                hideable: false,
                disableColumnMenu: false,
                getActions: ({ row }) => [
                    <GridActionsCellItem
                        icon={<Edit />}
                        label={t('Edit')}
                        sx={{
                            color: 'primary.main',
                        }}
                        onClick={() => handleEditProduct(row)}
                    />,
                    <GridActionsCellItem
                        icon={<Visibility />}
                        label={t('View')}
                        sx={{
                            color: 'primary.main',
                        }}
                        onClick={() => handleViewProduct(row)}
                    />,
                ],
            },
        ],
        [t, handleEditProduct, handleViewProduct, renderSimpleCell, renderDefaultLayoutCell],
    );

    // Memoize getRowId to prevent recreation
    const getRowId = useCallback((row: Product) => row.product_id, []);

    // Memoize table element to prevent re-renders when searchForm changes
    // The table only needs to update when its actual data/props change
    const tableElement = useMemo(
        () => (
            <BaseDataTable
                rows={products}
                columns={columns}
                loading={loading}
                getRowId={getRowId}
                paginationModel={paginationModel}
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                checkboxSelection
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
                onSortModelChange={sortModelChangeHandler}
                onPaginationModelChange={pageChangeHandler}
            />
        ),
        [
            products,
            columns,
            loading,
            getRowId,
            paginationModel,
            rowCount,
            columnVisibilityModel,
            selectedRows,
            sortModelChangeHandler,
            pageChangeHandler,
        ],
    );

    const selectedIds = (selectedRows as (string | number)[]).map((id) => (typeof id === 'string' ? Number(id) : id));
    const selectedProducts = products.filter((p) => selectedIds.includes(p.product_id));
    const deviceProducts = selectedProducts.filter((p) => p.has_devices);
    const visibleDevices = deviceProducts.slice(0, 5);
    const extraCount = Math.max(deviceProducts.length - visibleDevices.length, 0);

    return (
        <>
            <BasePaper isFullHeight={false} className="product-page">
                {/* Title Section */}
                <BaseTitle icon={<Redeem />} title={t('Stores Product')} />

                {manageStoreId ? (
                    <>
                        {/* Filter Section */}
                        <StoreFilterBlock handleSearch={applySearchAndFetch} handleClear={clearProducts}>
                            <BaseTextField
                                label={t('Barcode')}
                                value={searchForm.f_barcode}
                                onChange={(e) => updateSearchForm('f_barcode', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                            <BaseTextField
                                label={t('Number')}
                                value={searchForm.f_number}
                                onChange={(e) => updateSearchForm('f_number', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                            <BaseTextField
                                label={t('Name')}
                                value={searchForm.f_name}
                                onChange={(e) => updateSearchForm('f_name', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                            <BaseTextField
                                label={t('Area')}
                                value={searchForm.f_area}
                                onChange={(e) => updateSearchForm('f_area', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                            <BaseTextField
                                label={t('Unit')}
                                value={searchForm.f_unit}
                                onChange={(e) => updateSearchForm('f_unit', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                        </StoreFilterBlock>

                        {/* Table Section */}
                        <BaseCard className="table-card">
                            <div className="table-box">
                                <div className="actions-box">
                                    <div className="actions-buttons">
                                        <BaseButton
                                            variant="contained"
                                            color="error"
                                            startIcon={<Delete />}
                                            onClick={() => setIsConfirmDialogOpen(true)}
                                            disabled={selectedRows.length === 0}
                                        >
                                            {t('Remove')}
                                        </BaseButton>
                                        <BaseButton
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Add />}
                                            onClick={() => setIsModalOpen(true)}
                                        >
                                            {t('New Product')}
                                        </BaseButton>
                                        <BaseButton
                                            component="label"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Upload />}
                                            loading={importLoading}
                                            onClick={importClickHandler}
                                        >
                                            {t('Import')}
                                            {/* <VisuallyHiddenInput type="file" accept=".json" onChange={importHandler} /> */}
                                        </BaseButton>

                                        <BaseButton
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Download />}
                                            loading={exportLoading}
                                            onClick={() => {
                                                exportHandler();
                                            }}
                                        >
                                            {t('Export')}
                                        </BaseButton>

                                        {importProgressPercentage >= 0 && jobIdData.jobId && (
                                            <div className="import-progress">
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        display: 'inline-flex',
                                                    }}
                                                >
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={importProgressPercentage}
                                                    />
                                                    <Box
                                                        sx={{
                                                            top: 0,
                                                            left: 0,
                                                            bottom: 0,
                                                            right: 0,
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            component="div"
                                                            sx={{
                                                                color: 'text.secondary',
                                                            }}
                                                        >
                                                            {importProgressPercentage + '%'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <div className="import-progress-text">
                                                    {jobIdData.isExistingJob
                                                        ? t(
                                                              'An ongoing product import was detected. Resuming the previous product import.',
                                                          )
                                                        : t("Importing... Don't operate this page.")}
                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                        {' '}
                                                        {t('Job ID')}: {jobIdData.jobId}
                                                    </Typography>
                                                </div>
                                            </div>
                                        )}
                                        <Box sx={{ fontSize: '14px' }}>{renderJsonImportError()}</Box>
                                        <Box sx={{ fontSize: '14px' }}>{renderImportFamilyMartTfmCsv()}</Box>
                                        <Box sx={{ fontSize: '14px' }}>{renderImportLotussTfmCsv()}</Box>
                                        <Box sx={{ fontSize: '14px' }}>{renderImportTaiwanMobileXlsx()}</Box>
                                    </div>
                                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto', height: 'fit-content' }}>
                                        <PopoverMenu title={t('Product Type')} icon={<FilterList />}>
                                            <FormLabel sx={{ pl: 1.5 }}>{t('Type Filter')}</FormLabel>
                                            <FormGroup>
                                                {[{ label: 'HQ Products', value: 'rrr' }].map((option) => (
                                                    <FormControlLabel
                                                        key={option.label}
                                                        control={
                                                            <Checkbox
                                                                checked={searchForm.f_is_hq === '1'}
                                                                onChange={(_event) => {
                                                                    const newValue = _event.target.checked ? '1' : '0';
                                                                    updateSearchForm('f_is_hq', newValue);
                                                                    // Immediately apply filter for checkbox
                                                                    const updatedForm = {
                                                                        ...searchForm,
                                                                        f_is_hq: newValue,
                                                                    };
                                                                    searchFormRef.current = updatedForm;
                                                                    startTransition(() => {
                                                                        dispatch({
                                                                            type: 'SET_SEARCH_QUERY',
                                                                            payload: updatedForm,
                                                                        });
                                                                    });
                                                                    fetchProducts(updatedForm);
                                                                }}
                                                            />
                                                        }
                                                        label={option.label}
                                                        value={option.value}
                                                        sx={{ m: 0 }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </PopoverMenu>
                                    </Box>
                                </div>

                                {tableElement}
                            </div>
                        </BaseCard>
                    </>
                ) : (
                    <NoStoreSelectionBlock />
                )}
            </BasePaper>
            <BaseDialog
                dialogTitle={t('Remove Products')}
                open={isConfirmDialogOpen}
                maxWidth="sm"
                confirmButtonText={t('Confirm')}
                onConfirm={confirmRemoveProducts}
                onClose={() => setIsConfirmDialogOpen(false)}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography component="div">
                        <Trans
                            i18nKey="Are you sure you want to remove <strong>{{count}}</strong> products?"
                            values={{ count: selectedRows.length }}
                            components={{ strong: <strong /> }}
                        />
                    </Typography>

                    {deviceProducts.length > 0 && (
                        <>
                            <Box display="flex" alignItems="center" my={2}>
                                <WarningAmber
                                    color="warning"
                                    fontSize="small"
                                    sx={{
                                        mr: 1,
                                    }}
                                />
                                <Typography variant="body2" sx={{ lineHeight: 1 }}>
                                    {t('Electronic price tags will lose product info if you remove these items:')}
                                </Typography>
                            </Box>

                            <Box
                                component="ul"
                                sx={{
                                    listStyleType: 'disc',
                                    listStylePosition: 'inside',
                                    pl: 0,
                                    m: 0,
                                }}
                            >
                                {visibleDevices.map((prod) => (
                                    <Box
                                        component="li"
                                        key={prod.product_id}
                                        sx={{
                                            p: 0,
                                            display: 'list-item',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        [{prod.barcode}]&nbsp;
                                        <Box component="span" sx={{ fontStyle: prod.name ? 'normal' : 'italic' }}>
                                            {prod.name || t('No product')}
                                        </Box>
                                    </Box>
                                ))}

                                {extraCount > 0 && (
                                    <Box
                                        component="li"
                                        sx={{
                                            fontStyle: 'italic',
                                            p: 0,
                                            display: 'list-item',
                                            lineHeight: 2,
                                        }}
                                    >
                                        {t('andMoreProducts', { count: extraCount })}
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </BaseDialog>

            <BaseDialog
                dialogTitle={t('New Product')}
                open={isModalOpen}
                maxWidth="lg"
                confirmButtonText={t('Save')}
                cancelButtonText={t('Cancel')}
                onConfirm={handleAddProduct}
                onClose={() => setIsModalOpen(false)}
                PaperProps={{
                    style: {
                        width: '80%',
                        maxWidth: '1000px',
                        padding: '2rem',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        columnGap: '1.5rem',
                        rowGap: '0.8rem',
                        marginTop: '0.4rem',
                        width: '100%',
                    }}
                >
                    {Object.keys(newProductData).map((key) => (
                        <FormControl key={key} error={!!validatedMessage[key]}>
                            <BaseTextField
                                key={key}
                                label={t(key)}
                                required={checkIsRequired(key)}
                                error={!!validatedMessage[key]}
                                value={newProductData[key as keyof typeof newProductData]}
                                onChange={(e) => inputChangeHandler(e, key)}
                                style={{
                                    gridColumn: key === 'Custom Fields' ? 'span 2' : 'auto',
                                }}
                            />
                            <FormHelperText id="component-helper-text">
                                {validatedMessage[key] ? t(validatedMessage[key]) : ''}
                            </FormHelperText>
                        </FormControl>
                    ))}
                </Box>
            </BaseDialog>
            <BaseDialog
                dialogTitle={t('Remove Products Error & Reason')}
                open={isRemoveDialogOpen}
                confirmButtonText="OK"
                cancelButtonText=""
                onConfirm={() => {
                    dispatch({ type: 'SET_IS_REMOVE_DIALOG_OPEN', payload: false });
                }}
                TransitionProps={{
                    onExited: () => dispatch({ type: 'SET_INVALID_LIST', payload: [] }),
                }}
            >
                {(() => {
                    const visible = invalidList.slice(0, 10);
                    const hasMore = invalidList.length > visible.length;

                    return (
                        <Box
                            sx={{
                                border: (t) => `1px solid ${t.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            {/* header */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '150px 250px',
                                    px: 1,
                                    py: 0.5,
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {t('Barcode')}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {t('Reason')}
                                </Typography>
                            </Box>

                            {/* rows */}
                            <Box>
                                {visible.map((it, idx) => (
                                    <Box
                                        key={`${it.barcode}-${idx}`}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '150px 250px',
                                            px: 1,
                                            py: 0.5,
                                            borderTop: (t) => `1px solid ${t.palette.divider}`,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={it.barcode}
                                        >
                                            {it.barcode}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                // overflow: 'hidden',
                                                // textOverflow: 'ellipsis',
                                                whiteSpace: 'wrap',
                                            }}
                                            title={it.reason}
                                        >
                                            {it.reason}
                                        </Typography>
                                    </Box>
                                ))}

                                {hasMore && (
                                    <Box
                                        sx={{
                                            px: 1,
                                            py: 0.5,
                                            borderTop: (t) => `1px solid ${t.palette.divider}`,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {t('...and more')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })()}
            </BaseDialog>
            <MessageDialog
                isOpen={messageDialogData.isOpen}
                dialogTitle={t('Import File Failed')}
                message={messageDialogData.message}
                onClose={() => dispatch({ type: 'SET_MESSAGE_DIALOG_DATA', payload: { isOpen: false, message: '' } })}
            />

            <ImportDialog
                isOpen={isImportDialogOpen}
                importLoading={importLoading}
                storeSettings={storeSettings}
                onImport={importHandler}
                onClose={() => dispatch({ type: 'SET_IS_IMPORT_DIALOG_OPEN', payload: false })}
            />
            <EditProductDialog
                isDialogOpen={isEditDialogOpen}
                editData={selectedProduct}
                setEditData={setSelectedProduct}
                onConfirm={confirmEditProduct}
                onClose={() => setIsEditDialogOpen(false)}
            />
            <ViewProductDialog
                isDialogOpen={isViewDialogOpen}
                formatUtcToStoreTime={formatUtcToStoreTime}
                barcode={currentBarCode}
                onClose={handleViewProductClose}
            />
        </>
    );
};

export default StoreProductPage;
